/**
 * Sentry error tracking initialization
 *
 * Initializes Sentry for production error tracking, performance
 * monitoring, and session replay on errors.
 */
import * as Sentry from '@sentry/react';
import { createLogger } from './logger';

const log = createLogger('sentry');

/**
 * Initialize Sentry. Call once in main.jsx before React renders.
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn || import.meta.env.DEV) {
    log.info('Sentry skipped (no DSN or dev mode)');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    allowUrls: [/app\.thrivecompanions\.com/],
  });

  window.__SENTRY_INITIALIZED__ = true;
  log.info('Sentry initialized');
};

/**
 * Report an error to Sentry with optional context.
 * Safe to call even if Sentry is not initialized (dev mode).
 * @param {Error} err
 * @param {Object} [extra] - Additional context to attach
 */
export const reportError = (err, extra) => {
  if (window.__SENTRY_INITIALIZED__) {
    Sentry.captureException(err, extra ? { extra } : undefined);
  }
};

export { Sentry };
